/* Homepage interactions from the 19 Oct export. */
import { mountEnquire } from "./enquire";

export function mountLiveHome(root) {
  if (!root) return function () {};
  var added = [];
  var rafIds = [];
  var alive = true;
  var origAdd = EventTarget.prototype.addEventListener;
  var origRaf = window.requestAnimationFrame;
  EventTarget.prototype.addEventListener = function (type, fn, opts) {
    added.push([this, type, fn, opts]);
    return origAdd.call(this, type, fn, opts);
  };
  window.requestAnimationFrame = function (fn) {
    if (!alive) return 0;
    var id = origRaf.call(window, function (t) { if (alive) fn(t); });
    rafIds.push(id);
    return id;
  };
  try {
    boot(root);
  } finally {
    EventTarget.prototype.addEventListener = origAdd;
    window.requestAnimationFrame = origRaf;
  }
  return function () {
    alive = false;
    rafIds.forEach(function (id) { cancelAnimationFrame(id); });
    added.forEach(function (item) {
      item[0].removeEventListener(item[1], item[2], item[3]);
    });
  };
}

function boot(root) {

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var clamp = function (v, min, max) { return v < min ? min : v > max ? max : v; };
  var mod = function (a, n) { return ((a % n) + n) % n; };
  var raf = window.requestAnimationFrame.bind(window);

  /* ------------------------------------------------------------------------
     0. Header — tablet/mobile menu + floating header
     Desktop (≥1024px) keeps the inline navigation and a static header.
     Below that, a menu button opens a dropdown panel, and once the header has
     scrolled away it floats back in whenever the reader scrolls up.
     ---------------------------------------------------------------------- */
  (function () {
    var header = root.querySelector('[data-header]');
    if (!header) return;
    var slot = header.parentElement;
    var toggle = header.querySelector('[data-menu-toggle]');
    var menu = header.querySelector('[data-menu]');
    var backdrop = root.querySelector('[data-menu-backdrop]');
    var compact = window.matchMedia('(max-width: 1023px)');
    var open = false, closeTimer = null;

    function setOpen(next, returnFocus) {
      if (next === open || !menu) return;
      open = next;
      clearTimeout(closeTimer);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (open) {
        menu.hidden = false;
        backdrop.hidden = false;
        header.classList.remove('is-tucked');
        raf(function () { raf(function () { menu.classList.add('is-open'); backdrop.classList.add('is-open'); }); });
        var first = menu.querySelector('a');
        if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 60);
      } else {
        menu.classList.remove('is-open');
        backdrop.classList.remove('is-open');
        closeTimer = setTimeout(function () { if (!open) { menu.hidden = true; backdrop.hidden = true; } }, reduceMotion.matches ? 0 : 260);
        if (returnFocus) toggle.focus({ preventScroll: true });
      }
    }

    if (toggle && menu && backdrop) {
      toggle.addEventListener('click', function () { setOpen(!open, false); });
      backdrop.addEventListener('click', function () { setOpen(false, false); });
      menu.addEventListener('click', function (e) { if (e.target.closest('a')) setOpen(false, false); });
      document.addEventListener('keydown', function (e) {
        if (!open) return;
        if (e.key === 'Escape') { setOpen(false, true); return; }
        if (e.key === 'Tab') {                         // keep focus inside the open menu
          var items = [toggle].concat(Array.prototype.slice.call(menu.querySelectorAll('a')));
          var i = items.indexOf(document.activeElement);
          if (e.shiftKey && i <= 0) { e.preventDefault(); items[items.length - 1].focus(); }
          else if (!e.shiftKey && i === items.length - 1) { e.preventDefault(); items[0].focus(); }
        }
      });
    }

    // Floating header
    var floating = false, lastY = window.scrollY, ticking = false;
    function unfloat() {
      if (!floating) return;
      floating = false;
      header.classList.remove('is-anim', 'is-floating', 'is-tucked');
    }
    // Inner pages: the header pins to the top as soon as it would scroll away,
    // at every screen size, and stays visible.
    var pin = header.hasAttribute('data-header-pin');
    var pinned = false;
    function place() {
      var r = slot.getBoundingClientRect();
      header.style.left = r.left + 'px';
      header.style.width = r.width + 'px';
    }
    function updatePin() {
      var top = slot.getBoundingClientRect().top;
      var gap = 12 + (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sat')) || 0);
      var should = top <= gap;
      if (should !== pinned) {
        pinned = should;
        header.classList.toggle('is-pinned', pinned);
        if (pinned) place(); else { header.style.left = ''; header.style.width = ''; }
      } else if (pinned) place();
    }

    function update() {
      ticking = false;
      if (pin) { updatePin(); return; }
      var y = window.scrollY;
      if (!compact.matches) { unfloat(); lastY = y; return; }
      var r = slot.getBoundingClientRect();
      var slotTop = r.top + y, slotBottom = r.bottom + y;
      if (!floating) {
        if (y > slotBottom + 60) {
          floating = true;
          header.classList.add('is-floating', 'is-tucked');   // enter off-screen, no jump
          void header.offsetWidth;
          header.classList.add('is-anim');
        }
      } else if (y <= slotTop - 10 || (header.classList.contains('is-tucked') && y < slotBottom)) {
        unfloat();                                           // back in its natural place, seamlessly
      }
      if (floating && !open) {
        var dy = y - lastY;
        if (dy < -4) header.classList.remove('is-tucked');   // scrolling up: show
        else if (dy > 4) header.classList.add('is-tucked');  // scrolling down: get out of the way
      }
      lastY = y;
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; raf(update); } }, { passive: true });
    if (pin) window.addEventListener('resize', function () { if (pinned) place(); });
    var onMode = function () { if (!compact.matches) setOpen(false, false); update(); };
    if (compact.addEventListener) compact.addEventListener('change', onMode); else compact.addListener(onMode);
    update();
  })();

  /* ------------------------------------------------------------------------
     1. 3D hero slider
     A single continuous value `pos` drives every card. Card i sits at
     p = i - pos (wrapped to the nearest copy), and its transform is a pure
     function of p. Integer p reproduces the Figma collage exactly:
       p = 0   → 800 × 550 centred, flat, on top
       p = ±1  → scale .818182, centre ±272.5 / 1200 of the stage, flat
     Between those, cards arc back in Z and turn on Y, so motion has depth
     without changing the resting layout.
     ---------------------------------------------------------------------- */
  function HeroSlider3D(root) {
    this.root = root;
    this.stage = root.querySelector('.hero-slider__stage');
    this.cards = Array.prototype.slice.call(this.stage.children);
    this.images = this.cards.map(function (c) { return c.querySelector('img'); });
    this.status = root.querySelector('[data-hero-status]');
    this.n = this.cards.length;

    this.start = Math.floor(this.n / 2);   // Entertainment screen in the middle
    this.pos = this.start;                 // continuous position (in slides)
    this.target = this.start;
    this.vel = 0;                          // slides / second
    this.tilt = 0;                         // drag-velocity tilt (deg), eased
    this.width = 0;
    this.running = false;
    this.drag = null;
    this.lastActive = -1;

    this.SIDE_SCALE = 654.545 / 800;       // .818182
    this.SIDE_SHIFT = 272.5 / 1200;        // fraction of stage width per slide
    this.IMG_SIDE = 605.334 / 654.545;     // neighbour screenshot width in its frame

    this.measure();
    this.bind();
    this.render();
    root.classList.add('is-ready');
  }

  HeroSlider3D.prototype.measure = function () {
    this.width = this.root.getBoundingClientRect().width || 1;
  };

  HeroSlider3D.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('pointerdown', function (e) { self.onDown(e); });
    this.root.addEventListener('pointermove', function (e) { self.onMove(e); });
    this.root.addEventListener('pointerup', function (e) { self.onUp(e); });
    this.root.addEventListener('pointercancel', function (e) { self.onUp(e, true); });
    this.root.addEventListener('lostpointercapture', function (e) { if (self.drag) self.onUp(e, true); });
    // A click on a neighbour brings it to the front (ignored after a drag)
    this.root.addEventListener('click', function (e) {
      if (self.suppressClick) { self.suppressClick = false; return; }
      var card = e.target.closest('.screen');
      if (!card) return;
      var p = self.offsetOf(self.cards.indexOf(card));
      if (Math.abs(p) > 0.5) self.go(self.target + Math.round(p));
    });
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.target - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.target + 1); }
    });
    // Horizontal trackpad / shift+wheel gestures
    this.root.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 1) return;
      e.preventDefault();
      if (self.wheelLock) return;
      self.wheelLock = true;
      self.go(self.target + (e.deltaX > 0 ? 1 : -1));
      setTimeout(function () { self.wheelLock = false; }, 450);
    }, { passive: false });

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { self.measure(); self.render(); }).observe(this.root);
    } else {
      window.addEventListener('resize', function () { self.measure(); self.render(); });
    }
  };

  /* Shortest signed distance from the current position to card i */
  HeroSlider3D.prototype.offsetOf = function (i) {
    var half = this.n / 2;
    return mod(i - this.pos + half, this.n) - half;
  };

  HeroSlider3D.prototype.onDown = function (e) {
    if (e.button !== undefined && e.button !== 0) return;
    this.suppressClick = false;
    this.drag = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      pos0: this.pos,
      lastX: e.clientX,
      lastT: performance.now(),
      v: 0,
      mouse: e.pointerType === 'mouse',
      engaged: false                       // engage only once the pointer really moves, so clicks still work
    };
  };

  HeroSlider3D.prototype.engage = function (e) {
    this.drag.engaged = true;
    try { this.root.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    this.root.classList.add('is-dragging');
    this.running = false;              // pause the spring; the finger owns the position
  };

  HeroSlider3D.prototype.onMove = function (e) {
    var d = this.drag;
    if (!d || e.pointerId !== d.id) return;
    var dx = e.clientX - d.x0;
    if (!d.engaged) {
      var dy = e.clientY - d.y0;
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      if (!d.mouse && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }   // let the page scroll
      // Re-base so the cards don't jump by the dead-zone distance
      d.x0 = e.clientX - (dx > 0 ? 1 : -1);
      d.pos0 = this.pos;
      dx = e.clientX - d.x0;
      this.vel = 0;
      this.engage(e);
    }
    var step = this.width * this.SIDE_SHIFT;          // px per slide: card follows the pointer 1:1
    this.pos = d.pos0 - dx / step;

    var now = performance.now();
    var dt = Math.max(1, now - d.lastT) / 1000;
    var inst = -(e.clientX - d.lastX) / step / dt;    // slides / second
    d.v = d.v * 0.6 + inst * 0.4;                     // smoothed for a stable fling
    d.lastX = e.clientX;
    d.lastT = now;

    this.tilt = clamp(-d.v * 2.2, -10, 10);
    if (Math.abs(dx) > 5) this.suppressClick = true;
    this.render();
  };

  HeroSlider3D.prototype.onUp = function (e, cancelled) {
    var d = this.drag;
    if (!d || (e.pointerId !== undefined && e.pointerId !== d.id)) return;
    this.drag = null;
    this.root.classList.remove('is-dragging');
    if (!d.engaged) return;

    // Stale velocity if the pointer rested before release
    var idle = performance.now() - d.lastT;
    var v = idle > 80 ? 0 : d.v;
    // Project where a flick would carry the cards, then settle on a whole slide
    var projected = this.pos + clamp(v * 0.22, -1.6, 1.6);
    var tgt = Math.round(projected);
    if (!cancelled && Math.abs(this.pos - d.pos0) > 0.12 && tgt === Math.round(d.pos0) && Math.abs(v) > 0.6) {
      tgt += v > 0 ? 1 : -1;                         // a short, quick swipe still advances
    }
    this.vel = v;
    this.go(tgt, true);
  };

  HeroSlider3D.prototype.go = function (target, keepVelocity) {
    this.target = target;
    if (!keepVelocity) this.vel = this.vel * 0.5;
    if (reduceMotion.matches) {
      this.pos = target; this.vel = 0; this.tilt = 0;
      this.render(); this.normalise();
      return;
    }
    if (!this.running) {
      this.running = true;
      this.lastFrame = performance.now();
      raf(this.tick.bind(this));
    }
  };

  /* Critically damped spring — no overshoot jitter, continuous with release velocity */
  HeroSlider3D.prototype.tick = function (now) {
    if (!this.running) return;
    var dt = Math.min(0.032, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    var k = 120, c = 2 * Math.sqrt(k) * 1.0;          // stiffness, critical damping
    var a = k * (this.target - this.pos) - c * this.vel;
    this.vel += a * dt;
    this.pos += this.vel * dt;
    this.tilt += (clamp(-this.vel * 2.2, -10, 10) - this.tilt) * Math.min(1, dt * 10);

    if (Math.abs(this.target - this.pos) < 0.0004 && Math.abs(this.vel) < 0.004 && Math.abs(this.tilt) < 0.02) {
      this.pos = this.target; this.vel = 0; this.tilt = 0;
      this.running = false;
      this.render();
      this.normalise();
      return;
    }
    this.render();
    raf(this.tick.bind(this));
  };

  /* Keep numbers small after many laps */
  HeroSlider3D.prototype.normalise = function () {
    var shift = this.target - mod(this.target, this.n);
    this.target -= shift;
    this.pos -= shift;
  };

  HeroSlider3D.prototype.render = function () {
    var W = this.width, shift = this.SIDE_SHIFT, sideScale = this.SIDE_SCALE;
    var motion = !reduceMotion.matches;
    var activeIndex = mod(Math.round(this.pos), this.n);

    for (var i = 0; i < this.n; i++) {
      var p = this.offsetOf(i);
      var ap = Math.abs(p);
      var t = Math.min(ap, 1);                         // 0 → active, 1 → neighbour
      var beyond = Math.max(ap - 1, 0);                // only while wrapping round the back
      var sgn = p < 0 ? -1 : 1;

      var x = p * shift * W;
      var scale = 1 - (1 - sideScale) * t - 0.12 * beyond;
      var arc = Math.sin(t * Math.PI);                 // 0 at rest, 1 mid-transition
      var z = motion ? -arc * 0.11 * W - beyond * 0.15 * W : 0;
      var ry = motion ? sgn * arc * 34 + this.tilt * (1 - t * 0.5) : 0;
      var opacity = clamp(1 - beyond * 2.2, 0, 1);

      var card = this.cards[i];
      card.style.transform =
        'translate3d(' + x.toFixed(2) + 'px,0,' + z.toFixed(2) + 'px) rotateY(' + ry.toFixed(3) + 'deg) scale(' + scale.toFixed(5) + ')';
      card.style.opacity = opacity < 1 ? opacity.toFixed(3) : '';
      card.style.zIndex = String(100 - Math.round(ap * 20));
      card.style.visibility = opacity <= 0.001 ? 'hidden' : '';
      card.style.setProperty('--sheen', (motion ? arc * 0.9 : 0).toFixed(3));
      card.style.setProperty('--sheen-x', (sgn * (t * 140 - 70)).toFixed(1) + '%');
      card.setAttribute('aria-hidden', i === activeIndex ? 'false' : 'true');

      // Neighbour screenshots sit at 605.3px inside a 654.5px frame (Figma),
      // anchored to their outer edge; the active one fills the frame.
      var img = this.images[i];
      var imgScale = 1 - (1 - this.IMG_SIDE) * t;
      img.style.transformOrigin = p > 0 ? '100% 0' : '0 0';
      img.style.transform = imgScale < 0.99999 ? 'scale(' + imgScale.toFixed(5) + ')' : '';
    }

    if (activeIndex !== this.lastActive) {
      this.lastActive = activeIndex;
      if (this.status) {
        this.status.textContent = (this.cards[activeIndex].getAttribute('aria-label') || '') +
          ' screen, ' + (activeIndex + 1) + ' of ' + this.n;
      }
    }
  };

  var hero = root.querySelector('[data-hero-slider]');
  if (hero) {
    new HeroSlider3D(hero);

    /* Scroll-linked tilt (additive): flat at the top of the page, leaning back
       as the hero scrolls away. Transform only, so nothing reflows. */
    var motionOff = function () {
      return reduceMotion.matches || document.documentElement.getAttribute('data-motion') === 'off';
    };
    var ticking = false;
    var applyTilt = function () {
      ticking = false;
      if (motionOff()) { hero.style.transform = ''; return; }
      var rect = hero.getBoundingClientRect();
      var range = Math.max(1, rect.height * 1.2);
      // 0 while the slider sits where it loads; 1 once it has scrolled a slider-height up
      var start = hero.offsetTop - window.innerHeight * 0.35;
      var pr = clamp((window.scrollY - Math.max(0, start)) / range, 0, 1);
      if (window.scrollY <= 0 || pr === 0) { hero.style.transform = ''; return; }
      var e = pr * pr * (3 - 2 * pr);                // smoothstep
      hero.style.transform = 'perspective(1800px) rotateX(' + (e * 16).toFixed(2) + 'deg) translateY(' + (e * 36).toFixed(1) + 'px) scale(' + (1 - e * 0.06).toFixed(4) + ')';
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; raf(applyTilt); }
    }, { passive: true });
    applyTilt();
  }

  /* ------------------------------------------------------------------------
     2. About slider — stationary card; photo and text cross-fade together.
     Arrows, dots, keyboard, mouse drag and touch swipe all call goTo().
     ---------------------------------------------------------------------- */
  var slider = root.querySelector('[data-slider]');
  if (slider) {
    var viewport = slider.querySelector('.about__viewport');
    var slides = Array.prototype.slice.call(slider.querySelector('[data-track]').children);
    var dots = root.querySelectorAll('[data-dots] button');
    var count = slides.length;
    var index = 0, leaveTimer = null;

    var goTo = function (i, instant) {
      var next = mod(i, count);
      if (next === index && slides[next].classList.contains('is-active')) return;
      var prev = slides[index];
      clearTimeout(leaveTimer);
      slides.forEach(function (s) { s.classList.remove('is-leaving'); });
      if (!instant && prev !== slides[next]) prev.classList.add('is-leaving');
      prev.classList.remove('is-active');
      index = next;
      var cur = slides[index];
      var txt = cur.querySelector('.slide__text');
      if (txt) txt.style.transform = '';
      cur.classList.add('is-active');
      // Clear the outgoing slide once the new photo has fully faded in
      leaveTimer = setTimeout(function () { prev.classList.remove('is-leaving'); }, reduceMotion.matches ? 0 : 800);
      Array.prototype.forEach.call(dots, function (dot, d) {
        dot.classList.toggle('is-active', d === index);
        dot.setAttribute('aria-current', d === index ? 'true' : 'false');
      });
      slides.forEach(function (s, n) { s.setAttribute('aria-hidden', n === index ? 'false' : 'true'); });
    };

    // Preload every photo so a change never waits on the network
    slides.forEach(function (s) { var img = s.querySelector('img'); if (img) { img.loading = 'eager'; if (img.decode) img.decode().catch(function () {}); } });

    slider.querySelector('[data-prev]').addEventListener('click', function () { goTo(index - 1); });
    slider.querySelector('[data-next]').addEventListener('click', function () { goTo(index + 1); });
    Array.prototype.forEach.call(dots, function (dot, d) { dot.addEventListener('click', function () { goTo(d); }); });
    viewport.setAttribute('tabindex', '0');
    viewport.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
    });

    // Drag / swipe: the frame never moves; the text leans with the gesture
    var g = null;
    viewport.style.touchAction = 'pan-y';
    var lean = function (dx) {
      var txt = slides[index].querySelector('.slide__text');
      if (txt) txt.style.transform = dx ? 'translate3d(' + (dx * 0.12).toFixed(1) + 'px,0,0)' : '';
    };
    viewport.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      g = { id: e.pointerId, x0: e.clientX, y0: e.clientY, t0: performance.now(), dx: 0, on: false, mouse: e.pointerType === 'mouse' };
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!g || e.pointerId !== g.id) return;
      var dx = e.clientX - g.x0, dy = e.clientY - g.y0;
      if (!g.on) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (!g.mouse && Math.abs(dy) > Math.abs(dx)) { g = null; return; }
        g.on = true; viewport.setPointerCapture(e.pointerId); viewport.classList.add('is-dragging');
      }
      g.dx = clamp(dx, -160, 160);
      lean(g.dx);
    });
    var end = function (e) {
      if (!g || (e.pointerId !== undefined && e.pointerId !== g.id)) return;
      var gg = g; g = null;
      viewport.classList.remove('is-dragging');
      if (!gg.on) return;
      lean(0);
      var speed = gg.dx / Math.max(1, performance.now() - gg.t0);
      if (gg.dx < -50 || speed < -0.4) goTo(index + 1);
      else if (gg.dx > 50 || speed > 0.4) goTo(index - 1);
    };
    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);
    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // First slide is marked active in the HTML so the card renders before JS runs
    slides.forEach(function (s, n) { s.setAttribute('aria-hidden', n === 0 ? 'false' : 'true'); if (n) s.classList.remove('is-active'); });
    slides[0].classList.add('is-active');
  }

  /* ------------------------------------------------------------------------
     3. Buddyhood — continuous Rolodex carousel
     The seven cards (plus one aria-hidden copy of the set, so the loop never
     shows a gap) sit on a virtual strip of length L. A single `offset` value
     scrolls that strip; each card's position is wrapped modulo L, so the last
     card flows straight into the first with no jump.
     Each card is then bent onto a vertical cylinder of radius R:
       x = R·sin(θ), z = R·(cos θ − 1), rotateY(θ), with θ = s / R
     where s is the card's distance from the row centre along the strip.
     At s = 0 the card is flat, full size and exactly where the design puts it.
     ---------------------------------------------------------------------- */
  function Rolodex(row) {
    this.row = row;
    this.track = row.querySelector('.buddies__track');
    this.originals = Array.prototype.slice.call(this.track.children);
    this.GAP = 32;
    this.SPEED = 26;                 // px / second of continuous rotation
    this.cards = [];
    this.offset = 0;                 // strip position (px)
    this.vel = 0;                    // current drift (px / s)
    this.nudge = 0;                  // remaining distance from arrow presses (px)
    this.hover = false;
    this.visible = true;
    this.drag = null;
    this.running = false;

    // One copy of the whole set guarantees the strip is always wider than the
    // viewport, so no gap can ever open while a card wraps round.
    var self = this;
    this.originals.forEach(function (card) {
      var copy = card.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('img').forEach(function (img) { img.alt = ''; img.loading = 'eager'; });
      self.track.appendChild(copy);
    });
    this.originals.forEach(function (card) { var img = card.querySelector('img'); if (img) img.loading = 'eager'; });
    this.cards = Array.prototype.slice.call(this.track.children);

    row.classList.add('is-rolodex');
    this.measure();
    // Start exactly as designed: the seven-card group centred in the row
    this.offset = this.setWidth / 2;
    this.render();
    this.bind();
    this.start();
  }

  Rolodex.prototype.measure = function () {
    var self = this, x = 0;
    this.widths = this.cards.map(function (c) { return c.offsetWidth; });
    this.bases = this.widths.map(function (w) { var center = x + w / 2; x += w + self.GAP; return center; });
    this.L = x;                                        // full strip incl. trailing gap
    this.setWidth = x / 2 - this.GAP;                  // width of one seven-card group
    this.W = this.row.clientWidth;
    this.cx = this.W / 2;
    this.R = clamp(this.W * 0.86, 520, 1250);           // drum radius
    this.row.style.perspective = Math.round(clamp(this.W * 1.6, 900, 2400)) + 'px';
  };

  Rolodex.prototype.render = function () {
    var L = this.L, R = this.R, motion = !reduceMotion.matches;
    for (var i = 0; i < this.cards.length; i++) {
      var s = mod(this.bases[i] - this.offset + L / 2, L) - L / 2;   // distance from centre along the strip
      var w = this.widths[i];
      var x, z, ry;
      if (motion) {
        var th = clamp(s / R, -1.45, 1.45);
        x = R * Math.sin(th);
        z = R * (Math.cos(th) - 1);
        ry = th * 180 / Math.PI;
      } else {
        x = s; z = 0; ry = 0;
      }
      var left = this.cx + x - w / 2;
      var card = this.cards[i];
      card.style.transform = 'translate3d(' + left.toFixed(2) + 'px,0,' + z.toFixed(2) + 'px) rotateY(' + ry.toFixed(3) + 'deg)';
      card.style.zIndex = String(1000 - Math.round(Math.abs(s) / 10));
      card.style.visibility = Math.abs(s) > R * 1.45 ? 'hidden' : '';
    }
  };

  Rolodex.prototype.bind = function () {
    var self = this, row = this.row;

    var step = function () {
      // one card plus gap, measured from the card nearest the centre
      var nearest = 0, best = Infinity;
      for (var i = 0; i < self.cards.length; i++) {
        var d = Math.abs(mod(self.bases[i] - self.offset + self.L / 2, self.L) - self.L / 2);
        if (d < best) { best = d; nearest = i; }
      }
      return self.widths[nearest] + self.GAP;
    };
    var prev = root.querySelector('[data-buddies-prev]');
    var next = root.querySelector('[data-buddies-next]');
    if (prev) prev.addEventListener('click', function () { self.nudge -= step(); self.vel = 0; self.start(); });
    if (next) next.addEventListener('click', function () { self.nudge += step(); self.vel = 0; self.start(); });

    // Hovering pauses the rotation gently (desktop); leaving resumes it
    row.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') self.hover = true; });
    row.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') self.hover = false; });

    // Drag / swipe — the strip follows the pointer 1:1, then glides on
    row.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      self.drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, off0: self.offset, lastX: e.clientX, lastT: performance.now(), v: 0, on: false, mouse: e.pointerType === 'mouse' };
    });
    row.addEventListener('pointermove', function (e) {
      var d = self.drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x0, dy = e.clientY - d.y0;
      if (!d.on) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        if (!d.mouse && Math.abs(dy) > Math.abs(dx)) { self.drag = null; return; }   // vertical: let the page scroll
        d.on = true;
        d.x0 = e.clientX; d.off0 = self.offset; dx = 0;
        self.nudge = 0;
        try { row.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
        row.classList.add('is-dragging');
      }
      self.offset = d.off0 - dx;
      var now = performance.now();
      var inst = -(e.clientX - d.lastX) / Math.max(1, now - d.lastT) * 1000;   // px / s
      d.v = d.v * 0.6 + inst * 0.4;
      d.lastX = e.clientX; d.lastT = now;
      self.render();
    });
    var end = function (e) {
      var d = self.drag;
      if (!d || (e.pointerId !== undefined && e.pointerId !== d.id)) return;
      self.drag = null;
      row.classList.remove('is-dragging');
      if (!d.on) return;
      var idle = performance.now() - d.lastT;
      self.vel = idle > 80 ? 0 : clamp(d.v, -3200, 3200);   // fling, then eases back to the slow spin
      self.start();
    };
    row.addEventListener('pointerup', end);
    row.addEventListener('pointercancel', end);
    row.addEventListener('lostpointercapture', function (e) { if (self.drag) end(e); });
    row.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // Only animate while the section is on screen and the tab is visible
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible) self.start();
      }, { rootMargin: '200px 0px' }).observe(row);
    }
    document.addEventListener('visibilitychange', function () { if (!document.hidden) self.start(); });

    var lastW = row.clientWidth;
    var onResize = function () {
      if (row.clientWidth === lastW) return;
      lastW = row.clientWidth;
      self.measure();
      self.render();
    };
    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(row);
    else window.addEventListener('resize', onResize);
    // Card widths can settle after web fonts load
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.measure(); self.render(); });
  };

  Rolodex.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    raf(this.tick.bind(this));
  };

  Rolodex.prototype.tick = function (now) {
    if (!this.visible || document.hidden) { this.running = false; return; }
    var dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;

    if (!this.drag || !this.drag.on) {
      var cruise = (this.hover || reduceMotion.matches) ? 0 : this.SPEED;
      // Ease the drift toward the cruising speed: flings glide down, pauses settle gently
      this.vel += (cruise - this.vel) * (1 - Math.exp(-dt * 2.2));
      var move = this.vel * dt;
      if (this.nudge !== 0) {
        var part = this.nudge * (1 - Math.exp(-dt * 7));
        if (Math.abs(this.nudge - part) < 0.3) part = this.nudge;
        this.nudge -= part;
        move += part;
      }
      this.offset = mod(this.offset + move, this.L);
      this.render();
      // Idle (reduced motion, nothing pending): stop the loop until the next interaction
      if (reduceMotion.matches && this.nudge === 0 && Math.abs(this.vel) < 0.5) { this.vel = 0; this.running = false; return; }
    }
    raf(this.tick.bind(this));
  };

  var row = root.querySelector('[data-buddies]');
  if (row) new Rolodex(row);

  /* Enquire Now — same dialog and submit path as every other page. */
  mountEnquire(root);

  /* ------------------------------------------------------------------------
     5. Help Center — live search across the FAQs
     ---------------------------------------------------------------------- */
  (function () {
    var input = root.querySelector('[data-help-search]');
    if (!input) return;
    var items = Array.prototype.slice.call(root.querySelectorAll('[data-faq]'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-faq-group]'));
    var status = root.querySelector('[data-help-results]');
    var empty = root.querySelector('[data-help-empty]');
    input.addEventListener('input', function () {
      var q = input.value.trim().toLowerCase();
      var shown = 0;
      items.forEach(function (d) {
        var hit = !q || d.textContent.toLowerCase().indexOf(q) !== -1;
        d.hidden = !hit;
        if (hit) shown++;
        if (q && hit) d.open = true;
        if (!q) d.open = false;
      });
      groups.forEach(function (g) { g.hidden = !g.querySelector('[data-faq]:not([hidden])'); });
      status.textContent = q ? (shown ? shown + (shown === 1 ? ' answer' : ' answers') + ' found' : '') : '';
      empty.hidden = !(q && !shown);
    });
  })();
}
